
/**
 * Hides GiftShip's "Ship to Multiple Addresses" button in the drawer cart
 * when a GiftShip conditional cart-option block signals that it should be
 * hidden (e.g. because the cart contains a specific product).
 *
 * GiftShip's drawer cart is a React component we can't script directly, so
 * this module listens for GiftShip's public `giftship.*` events and toggles
 * a class on `#gsDrawer`. The actual hiding is done in CSS
 * (hide-ms-drawer-cart.liquid), scoped to that class — this script only
 * decides *whether* to hide, never *how*.
 *
 * Two GiftShip quirks this script works around, in order of how it found them:
 *
 * 1. Disabled blocks stay in the DOM. When a cart-option block's display
 *    rule doesn't match, GiftShip doesn't remove it — it adds a
 *    `gs__hidden-option` class to the wrapper and `disabled` to the input.
 *    A plain `querySelector` presence check would therefore always return
 *    true. HIDDEN_INPUT_SELECTOR excludes that disabled state explicitly.
 *
 * 2. The drawer's className gets clobbered after open/close. Right after
 *    `giftship.drawer-cart.opened`/`closed` fires, GiftShip's React re-render
 *    (likely toggling its own open/close animation class) overwrites
 *    `#gsDrawer`'s className wholesale — silently dropping any class this
 *    script added moments earlier. This is why the bug was intermittent:
 *    it only showed up on drawer open/close clicks, never on a fresh page
 *    load, because there's no competing re-render to race against at load
 *    time. Fixed with a MutationObserver that watches the drawer's `class`
 *    attribute and reasserts our state whenever something else touches it.
 */

(() => {
    var MODULE = {

        // data-child-name scopes this to the specific block named "hide_ms"
        // in GiftShip's admin, so an unrelated hidden block added later won't
        // trigger this script. :not(.gs__hidden-option) is required — see
        // quirk (1) above.

        HIDDEN_INPUT_SELECTOR: '#gsDrawer .gs__cart-option[data-child-type="hidden"][data-child-name="hide_ms"]:not(.gs__hidden-option)',
        HIDE_CLASS: 'gs__hide-ms-btn',

        // Tracks the MutationObserver from quirk (2) and the drawer node it's
        // currently bound to, so we can rebind if GiftShip ever swaps the node.

        observer: null,
        observedDrawer: null,

        init: function () {
            this.registerEventHandlers();
            this.registerElements();
        },

        registerEventHandlers: function () {
            document.addEventListener('giftship.drawer-cart.ready', this.checkHiddenInput.bind(this));
            document.addEventListener('giftship.drawer-cart.opened', this.checkHiddenInput.bind(this));
            document.addEventListener('giftship.drawer-cart.item-updated', this.checkHiddenInput.bind(this));
        },

        registerElements: function () {

            // Run once immediately in case a relevant event already fired
            // before this script finished loading (e.g. on a slow connection).
            
            this.checkHiddenInput();
        },

        checkHiddenInput: function () {
            var drawer = document.getElementById('gsDrawer');
            if (!drawer) return;

            this.applyState(drawer);
            this.watchDrawer(drawer);
        },

        // Adds or removes HIDE_CLASS to match whether the hidden cart-option
        // block is currently active. Guarded so it's a no-op when the class
        // already matches — keeps the MutationObserver below from looping.
        applyState: function (drawer) {
            var found = !!document.querySelector(this.HIDDEN_INPUT_SELECTOR);
            if (drawer.classList.contains(this.HIDE_CLASS) !== found) {
                drawer.classList.toggle(this.HIDE_CLASS, found);
            }
        },

        // See quirk (2) above. Watches the drawer's `class` attribute and
        // reapplies our state whenever GiftShip's own re-render overwrites it.
        watchDrawer: function (drawer) {
            if (this.observedDrawer === drawer) return;
            if (this.observer) this.observer.disconnect();

            var self = this;
            this.observer = new MutationObserver(function () {
                self.applyState(drawer);
            });
            this.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
            this.observedDrawer = drawer;
        },
    };

    MODULE.init();
})();
